/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXAtom, useStateXValue } from '@cloudio-saas/statex';
import { Box, SxProps, Theme } from '@mui/material';
import { Platform } from 'componentregistry';
import Controller from 'controller/Controller';
import { ConsoleErrorBoundary } from 'platform/ErrorBoundary';
import { usePlatform } from 'platform/PlatformHooks';
import React, { memo, useEffect } from 'react';
import useUser, { UserSession } from 'root/useUser';
import { useTheme } from 'theme/Theme';
import { getURLParam } from 'utils/url';
import { WithBootstrap } from '../../root/bs/WithBootstrap';
import { WithTailwind } from '../../root/tw/WithTailwind';
import { pageSXAtom } from 'designer/atoms';

export interface IOComponentProps {
  platform: Platform;
  appUid: string;
  pageId: string;
  theme: Theme;
  user: UserSession;
}

const PageController = memo(
  ({ appUid, pageId }: { appUid: string; pageId: string }) => {
    const platform = usePlatform();
    const user = useUser();
    const PageComp = Controller.getExport<React.FunctionComponent<any>>(
      appUid,
      pageId,
      'PageComponent',
    );
    return PageComp ? (
      <PageComp
        appUid={appUid}
        pageId={pageId}
        platform={platform}
        theme={platform.theme}
        user={user}
      />
    ) : null;
  },
);

export function PageCSS({
  children,
  appUid,
  pageId,
  withTailwind,
  withBootstrap,
}: {
  appUid: string;
  pageId: string;
  children: React.ReactNode[];
  withTailwind: boolean;
  withBootstrap: boolean;
}) {
  const skipCustomCode = getURLParam('skipCustomCode') === 'Y';
  const theme = useTheme();
  const platform = usePlatform();
  const [sx, setSx] = useStateXAtom(pageSXAtom, {
    params: { appUid, pageId },
  });
  const controller = useStateXValue(
    ['db', 'IoPages', pageId, 'controller'],
    '',
  );

  useEffect(() => {
    if (skipCustomCode) return;
    Controller.callAsyncIfExists({
      platform,
      appUid,
      pageId,
      functionName: 'getPageSX',
      itemId: 'pageCSS',
      props: {},
    }).then((response) => {
      if (response.status === 'SUCCESS' && response.result) {
        setSx(response.result as SxProps<Theme>);
      } else {
        setSx({});
      }
    });
  }, [appUid, controller, platform, theme, pageId, skipCustomCode]);
  const main = (
    <Box
      className="page-sx"
      sx={{
        position: 'relative',
        ...sx,
      }}
    >
      {skipCustomCode ? null : (
        <ConsoleErrorBoundary platform={platform} name="PageController">
          <PageController appUid={appUid} pageId={pageId} />
        </ConsoleErrorBoundary>
      )}
      {children}
    </Box>
  );

  return withTailwind ? (
    <WithTailwind>{main}</WithTailwind>
  ) : withBootstrap ? (
    <WithBootstrap>{main}</WithBootstrap>
  ) : (
    main
  );
}
